'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/**
 * A phone-only sticky action bar pinned to the bottom thumb zone — the primary
 * CTA is always reachable without scrolling to the end of a form.
 *
 * It portals to `document.body` on purpose: the creation card lives inside
 * transformed/animated ancestors (framer's `layout`, the route `PageTransition`),
 * and a `position: fixed` element inside a transformed ancestor is positioned
 * relative to that ancestor, not the viewport — so a naive fixed bar would drift
 * or scroll away. Rendering through a portal sidesteps that entirely.
 *
 * Hidden ≥ sm (there the inline button in the form is used instead). Respects the
 * iOS home-indicator safe area.
 */
export function MobileActionBar({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 sm:hidden',
        'border-t border-[var(--border)] bg-[color-mix(in_srgb,var(--bg)_90%,transparent)] backdrop-blur-xl',
        'px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}
